import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { API } from "../App";
import { ArrowLeft, Shield, Plus, Trash2, Save, Check, IndianRupee, TrendingUp } from "lucide-react";

const EMPTY = {
  name: "",
  logo_url: "",
  commission_pct: 10,
  referral_link_template: "",
  plan_summary: "",
  coverage_max: 100000,
  monthly_premium_from: 199,
  features: [],
  active: true,
};

export default function AdminInsurance() {
  const [partners, setPartners] = useState([]);
  const [referrals, setReferrals] = useState({ items: [], stats: {} });
  const [editing, setEditing] = useState(null); // partner obj or EMPTY
  const [saving, setSaving] = useState(false);

  const loadAll = useCallback(async () => {
    try {
      const [p, r] = await Promise.all([
        axios.get(`${API}/admin/insurance-partners`, { withCredentials: true }),
        axios.get(`${API}/admin/insurance-referrals`, { withCredentials: true }),
      ]);
      setPartners(p.data?.items || []);
      setReferrals(r.data || { items: [], stats: {} });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load data");
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  async function savePartner() {
    if (!editing?.name || !editing?.referral_link_template) {
      toast.error("Name and referral link template are required");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...editing, features: (editing.features || []).filter(Boolean) };
      if (editing.partner_id) {
        await axios.put(`${API}/admin/insurance-partners/${editing.partner_id}`, payload, { withCredentials: true });
        toast.success("Partner updated");
      } else {
        await axios.post(`${API}/admin/insurance-partners`, payload, { withCredentials: true });
        toast.success("Partner added");
      }
      setEditing(null);
      await loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  }

  async function deletePartner(id) {
    if (!window.confirm("Delete this insurance partner?")) return;
    try {
      await axios.delete(`${API}/admin/insurance-partners/${id}`, { withCredentials: true });
      toast.success("Partner removed");
      await loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  }

  async function markConverted(refId) {
    const commission = window.prompt("Commission earned (₹). 0 to keep as clicked.");
    if (commission === null) return;
    const commissionInt = parseInt(commission, 10) || 0;
    try {
      await axios.post(
        `${API}/admin/insurance-referrals/${refId}/convert`,
        { commission_earned: commissionInt, status: commissionInt > 0 ? "converted" : "clicked" },
        { withCredentials: true },
      );
      toast.success("Referral updated");
      await loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    }
  }

  async function markPaid(refId) {
    if (!window.confirm("Mark this referral commission as PAID?")) return;
    const ref = referrals.items.find(r => r.referral_id === refId);
    try {
      await axios.post(
        `${API}/admin/insurance-referrals/${refId}/convert`,
        { commission_earned: ref?.commission_earned || 0, status: "paid" },
        { withCredentials: true },
      );
      toast.success("Marked as paid");
      await loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    }
  }

  return (
    <div className="space-y-6" data-testid="admin-insurance-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Link to="/app/admin" data-testid="insurance-back" className="rounded-full px-3 py-1.5 text-sm font-bold border border-line bg-surface text-ink-soft hover:border-brand hover:text-ink inline-flex items-center gap-2">
          <ArrowLeft className="w-4 h-4"/>Back to Admin
        </Link>
        <button
          type="button"
          data-testid="add-insurance-partner"
          onClick={() => setEditing({ ...EMPTY })}
          className="rounded-full px-4 py-2 text-sm font-bold border bg-ink text-white border-ink hover:bg-brand hover:border-brand inline-flex items-center gap-2"
        >
          <Plus className="w-4 h-4"/>Add partner
        </button>
      </div>

      <header className="rounded-3xl bg-gradient-to-br from-ink to-[#1a1a1a] text-white p-6">
        <div className="flex items-center gap-3 text-brand text-xs font-bold tracking-widest uppercase"><Shield className="w-4 h-4"/>Insurance</div>
        <h1 className="font-heading text-2xl font-extrabold mt-1">Partners & commission</h1>
      </header>

      {/* Stats strip */}
      <section className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="referrals-stats">
        <Stat label="Total clicks" value={referrals.stats?.total_clicks || 0}/>
        <Stat label="Converted" value={referrals.stats?.converted || 0} icon={<TrendingUp className="w-4 h-4 text-brand-green"/>}/>
        <Stat label="Conversion %" value={`${referrals.stats?.conversion_pct || 0}%`}/>
        <Stat label="Commission earned" value={`₹${(referrals.stats?.total_commission_inr || 0).toLocaleString()}`} icon={<IndianRupee className="w-4 h-4 text-brand"/>}/>
      </section>

      {/* Partner list */}
      <section className="bg-surface border border-line rounded-3xl p-5 shadow-soft">
        <h2 className="font-heading text-lg font-extrabold mb-3">Partners ({partners.length})</h2>
        {partners.length === 0 ? (
          <div className="text-sm text-ink-soft">No partners yet. Add one to start earning commission from insurance referrals.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-[11px] font-bold uppercase tracking-wider text-ink-soft border-b border-line">
                <tr>
                  <th className="py-2">Partner</th>
                  <th>Commission</th>
                  <th>Premium from</th>
                  <th>Coverage max</th>
                  <th>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {partners.map(p => (
                  <tr key={p.partner_id} data-testid={`partner-row-${p.partner_id}`} className="border-b border-line/60 last:border-0">
                    <td className="py-3 font-bold">{p.name}</td>
                    <td>{p.commission_pct}%</td>
                    <td>₹{p.monthly_premium_from?.toLocaleString()}</td>
                    <td>₹{p.coverage_max?.toLocaleString()}</td>
                    <td>
                      <span className={`text-[10px] font-bold rounded-full px-2 py-0.5 ${p.active ? "bg-brand-green/20 text-brand-green" : "bg-surface-muted text-ink-soft"}`}>
                        {p.active ? "ACTIVE" : "INACTIVE"}
                      </span>
                    </td>
                    <td className="text-right">
                      <button type="button" data-testid={`edit-${p.partner_id}`} onClick={() => setEditing({ ...p })} className="text-xs font-bold text-brand mr-3 hover:underline">Edit</button>
                      <button type="button" data-testid={`delete-${p.partner_id}`} onClick={() => deletePartner(p.partner_id)} className="text-xs text-danger hover:underline inline-flex items-center gap-1"><Trash2 className="w-3 h-3"/>Delete</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Referrals list */}
      <section className="bg-surface border border-line rounded-3xl p-5 shadow-soft">
        <h2 className="font-heading text-lg font-extrabold mb-3">Referral clicks ({referrals.items.length})</h2>
        {referrals.items.length === 0 ? (
          <div className="text-sm text-ink-soft">No clicks yet.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-[11px] font-bold uppercase tracking-wider text-ink-soft border-b border-line">
                <tr>
                  <th className="py-2">When</th>
                  <th>User</th>
                  <th>Partner</th>
                  <th>Status</th>
                  <th>Commission (₹)</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {referrals.items.slice(0, 100).map(r => (
                  <tr key={r.referral_id} data-testid={`referral-${r.referral_id}`} className="border-b border-line/60 last:border-0">
                    <td className="py-2 text-ink-soft text-xs">{new Date(r.clicked_at).toLocaleString()}</td>
                    <td className="font-bold">{r.user_name}</td>
                    <td>{r.partner_name}</td>
                    <td>
                      <span className={`text-[10px] font-bold rounded-full px-2 py-0.5 ${r.status === "paid" ? "bg-brand-green/20 text-brand-green" : r.status === "converted" ? "bg-brand/20 text-brand" : "bg-surface-muted text-ink-soft"}`}>
                        {r.status?.toUpperCase()}
                      </span>
                    </td>
                    <td>₹{(r.commission_earned || 0).toLocaleString()}</td>
                    <td className="text-right">
                      {r.status === "clicked" && (
                        <button type="button" data-testid={`convert-${r.referral_id}`} onClick={() => markConverted(r.referral_id)} className="text-xs font-bold text-brand hover:underline">Mark converted</button>
                      )}
                      {r.status === "converted" && (
                        <button type="button" data-testid={`paid-${r.referral_id}`} onClick={() => markPaid(r.referral_id)} className="text-xs font-bold text-brand-green hover:underline inline-flex items-center gap-1"><Check className="w-3 h-3"/>Mark paid</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Editor modal */}
      {editing && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" data-testid="partner-editor" onClick={e => { if (e.target === e.currentTarget) setEditing(null); }}>
          <div className="bg-surface border border-line rounded-3xl w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-soft">
            <div className="p-6 space-y-3">
              <h3 className="font-heading text-xl font-extrabold">{editing.partner_id ? "Edit partner" : "Add insurance partner"}</h3>
              <Field label="Partner name *"><input data-testid="partner-name" required value={editing.name} onChange={e => setEditing({ ...editing, name: e.target.value })} className="li" placeholder="Digit Pet Insurance"/></Field>
              <Field label="Logo URL"><input data-testid="partner-logo" value={editing.logo_url || ""} onChange={e => setEditing({ ...editing, logo_url: e.target.value })} className="li" placeholder="https://…/logo.png"/></Field>
              <Field label="Referral link template *  (use {user_id} + {pet_id} placeholders)">
                <input data-testid="partner-link" required value={editing.referral_link_template} onChange={e => setEditing({ ...editing, referral_link_template: e.target.value })} className="li" placeholder="https://partner.com/quote?ref=PB-{user_id}&pet={pet_id}"/>
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Commission %"><input data-testid="partner-commission" type="number" step="0.5" value={editing.commission_pct} onChange={e => setEditing({ ...editing, commission_pct: parseFloat(e.target.value) || 0 })} className="li"/></Field>
                <Field label="Monthly premium from (₹)"><input data-testid="partner-premium" type="number" value={editing.monthly_premium_from} onChange={e => setEditing({ ...editing, monthly_premium_from: parseInt(e.target.value) || 0 })} className="li"/></Field>
              </div>
              <Field label="Coverage max (₹)"><input data-testid="partner-coverage" type="number" value={editing.coverage_max} onChange={e => setEditing({ ...editing, coverage_max: parseInt(e.target.value) || 0 })} className="li"/></Field>
              <Field label="Plan summary (shown to parents)"><textarea data-testid="partner-summary" rows={2} value={editing.plan_summary || ""} onChange={e => setEditing({ ...editing, plan_summary: e.target.value })} className="li"/></Field>
              <Field label="Features (one per line)"><textarea data-testid="partner-features" rows={3} value={(editing.features || []).join("\n")} onChange={e => setEditing({ ...editing, features: e.target.value.split("\n") })} className="li"/></Field>
              <label className="flex items-center gap-2 text-sm font-bold cursor-pointer">
                <input data-testid="partner-active" type="checkbox" checked={editing.active} onChange={e => setEditing({ ...editing, active: e.target.checked })}/>
                Active (visible to parents)
              </label>
              <div className="flex gap-2 pt-2">
                <button type="button" onClick={() => setEditing(null)} className="rounded-full px-4 py-2 text-sm font-bold border border-line text-ink-soft hover:text-ink">Cancel</button>
                <button type="button" data-testid="partner-save" onClick={savePartner} disabled={saving} className="rounded-full px-4 py-2 text-sm font-bold bg-ink text-white hover:bg-brand inline-flex items-center gap-2 disabled:opacity-50">
                  <Save className="w-4 h-4"/>{saving ? "Saving…" : "Save partner"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .li { width: 100%; padding: 0.55rem 0.85rem; border: 1px solid var(--line, #e6e8eb); border-radius: 0.75rem; background: #fff; font-size: 0.875rem; }
        .li:focus { outline: 2px solid #C24107; outline-offset: 1px; }
      `}</style>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-[11px] font-bold text-ink-soft mb-1">{label}</div>
      {children}
    </label>
  );
}

function Stat({ label, value, icon }) {
  return (
    <div className="bg-surface border border-line rounded-2xl p-4">
      <div className="text-[10px] font-bold uppercase tracking-wider text-ink-soft flex items-center gap-1.5">{icon}{label}</div>
      <div className="font-heading text-2xl font-extrabold mt-1">{value}</div>
    </div>
  );
}
